import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const HtmlEscapeUnescapePage = () => {
  const [htmlInput, setHtmlInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isEscapeMode, setIsEscapeMode] = useState(true);
  const fileInputRef = useRef(null);
  
  // Refs for scroll syncing
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);
  
  // Enhanced scroll synchronization
  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }

    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      
      outputRef.current.scrollTop = outputScrollTop;
      
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }

    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      
      inputRef.current.scrollTop = inputScrollTop;
      
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };
  
  // HTML escape function
  const escapeHtml = (text) => {
    // First, clean up escape sequences and normalize quotes
    let cleaned = text
      .replace(/\\n/g, '\n')         // Convert \n to actual newlines
      .replace(/\\r/g, '\r')         // Convert \r to actual carriage returns
      .replace(/\\t/g, '\t')         // Convert \t to actual tabs
      .replace(/\\"/g, "'")          // Convert \" to single quotes
      .replace(/\\'/g, "'")          // Convert \' to single quotes
      .replace(/\\\\/g, '\\');       // Convert \\ to single backslash
    
    const htmlEntities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
      '`': '&#x60;',
      '=': '&#x3D;'
    };
    
    return cleaned.replace(/[&<>"'`=\/]/g, (match) => htmlEntities[match]);
  };
  
  // HTML unescape function
  const unescapeHtml = (text) => {
    let result = text;
    
    // First, handle JavaScript-style escape sequences (like escape function does but reverse)
    result = result
      .replace(/\\n/g, '\n')         // Convert \n to actual newlines
      .replace(/\\r/g, '\r')         // Convert \r to actual carriage returns
      .replace(/\\t/g, '\t')         // Convert \t to actual tabs
      .replace(/\\"/g, '"')          // Convert \" to double quotes
      .replace(/\\'/g, "'")          // Convert \' to single quotes
      .replace(/\\\\/g, '\\');       // Convert \\ to single backslash
    
    // Then handle HTML entities
    const htmlEntities = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&#x2F;': '/',
      '&#x60;': '`',
      '&#x3D;': '=',
      '&apos;': "'",
      // Common named entities
      '&nbsp;': ' ',
      '&copy;': '©',
      '&reg;': '®',
      '&trade;': '™',
      '&mdash;': '—',
      '&ndash;': '–',
      '&hellip;': '…',
      '&laquo;': '«',
      '&raquo;': '»',
      '&bull;': '•',
      '&deg;': '°',
      '&plusmn;': '±',
      '&frac12;': '½',
      '&frac14;': '¼',
      '&frac34;': '¾',
      '&times;': '×',
      '&divide;': '÷'
    };
    
    // Handle named entities
    Object.keys(htmlEntities).forEach(entity => {
      const regex = new RegExp(entity, 'g');
      result = result.replace(regex, htmlEntities[entity]);
    });
    
    // Handle numeric entities (decimal)
    result = result.replace(/&#(\d+);/g, (match, dec) => {
      return String.fromCharCode(parseInt(dec, 10));
    });
    
    // Handle hexadecimal entities
    result = result.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });
    
    return result;
  };
  
  // Process the HTML based on mode
  const processHtml = () => {
    if (!htmlInput.trim()) {
      setError('Please enter HTML text to process.');
      setOutput('');
      return;
    }
    
    try {
      setError('');
      
      if (isEscapeMode) {
        const escaped = escapeHtml(htmlInput);
        setOutput(escaped);
      } else {
        const unescaped = unescapeHtml(htmlInput);
        setOutput(unescaped);
      }
    } catch (e) {
      setError('Error processing HTML: ' + e.message);
      setOutput('');
    }
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHtmlInput(e.target.result);
        setError('');
      };
      reader.readAsText(file);
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  const downloadOutput = () => {
    try {
      const content = output || htmlInput;
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isEscapeMode ? 'escaped-html.txt' : 'unescaped-html.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Error downloading: ' + e.message);
    }
  };
  
  const handleCopyOutput = () => {
    const textToCopy = output || htmlInput;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      setError('Failed to copy text: ' + err.message);
    });
  };
  
  const loadEscapeExample = () => {
    setHtmlInput(`<html>\\n<head>\\n</head>\\n<body onload=\\"document.ThreeDPostForm.submit()\\">\\n<form name=\\"ThreeDPostForm\\" method=\\"post\\" action=\\"https://sanalpos2.test.com.tr/fim/est3Dgate\\">\\n<input type=\\"hidden\\" name=\\"clientId\\" value=\\"191536973\\">\\n<input type=\\"hidden\\" name=\\"storeType\\" value=\\"3D\\">\\n<input type=\\"hidden\\" name=\\"oid\\" value=\\"123123\\">\\n</form>\\n</body>\\n</html>`);
    setIsEscapeMode(true);
    setError('');
  };
  
  const loadUnescapeExample = () => {
    setHtmlInput(`&lt;html&gt;\\n&lt;head&gt;\\n&lt;/head&gt;\\n&lt;body onload=&quot;document.ThreeDPostForm.submit()&quot;&gt;\\n&lt;form name=&quot;ThreeDPostForm&quot; method=&quot;post&quot; action=&quot;https://example.com&quot;&gt;\\n&lt;input type=&quot;hidden&quot; name=&quot;clientId&quot; value=&quot;123456&quot;&gt;\\n&lt;input type=&quot;hidden&quot; name=&quot;amount&quot; value=&quot;100.00&quot;&gt;\\n&lt;/form&gt;\\n&lt;/body&gt;\\n&lt;/html&gt;`);
    setIsEscapeMode(false);
    setError('');
  };
  
  const clearAll = () => {
    setHtmlInput('');
    setOutput('');
    setError('');
  };
  
  const switchMode = (escapeMode) => {
    setIsEscapeMode(escapeMode);
    setOutput('');
    setError('');
  };
  
  return (
    <div>
      <SEO 
        title="HTML Escape/Unescape Tool - Convert HTML Entities Online" 
        description="Free online tool to escape and unescape HTML entities. Convert HTML special characters to entities and vice versa safely."
        keywords="html escape, html unescape, html entities, escape html, unescape html, html encoder, html decoder"
      />
      
      <div className="container" style={{ maxWidth: '%95', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>HTML Escape/Unescape Tool</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Convert HTML special characters to entities and vice versa. Safely escape or unescape HTML content.
        </p>
        

        <div className="main-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="tab-container" style={{ borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
            <div className="tab-selector" style={{ display: 'flex', gap: '0' }}>
              <button 
                onClick={() => switchMode(true)}
                style={{ 
                  padding: '12px 24px', 
                  background: isEscapeMode ? '#4CAF50' : 'transparent', 
                  color: isEscapeMode ? 'white' : '#666', 
                  border: 'none', 
                  borderBottom: isEscapeMode ? '3px solid #4CAF50' : '3px solid transparent',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: isEscapeMode ? '600' : '400',
                  transition: 'all 0.3s ease'
                }}
              >
                📝 Escape HTML
              </button>
              
              <button 
                onClick={() => switchMode(false)}
                style={{ 
                  padding: '12px 24px', 
                  background: !isEscapeMode ? '#4CAF50' : 'transparent', 
                  color: !isEscapeMode ? 'white' : '#666', 
                  border: 'none', 
                  borderBottom: !isEscapeMode ? '3px solid #4CAF50' : '3px solid transparent',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: !isEscapeMode ? '600' : '400',
                  transition: 'all 0.3s ease'
                }}
              >
                🔓 Unescape HTML
              </button>
            </div>
          </div>
          
          <div className="controls" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', marginBottom: '10px' }}>
            <div className="button-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <button 
                onClick={processHtml}
                style={{ 
                  padding: '8px 16px', 
                  background: '#2196F3', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {isEscapeMode ? 'Escape HTML' : 'Unescape HTML'}
              </button>
              
              <button 
                onClick={handleUploadClick}
                style={{ 
                  padding: '8px 16px', 
                  background: '#9c27b0', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Upload File
              </button>
              
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                accept=".html,.htm,.txt"
              />
              
              <button 
                onClick={isEscapeMode ? loadEscapeExample : loadUnescapeExample}
                style={{ 
                  padding: '8px 16px', 
                  background: '#ff9800', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Load Example
              </button>
              
              <button 
                onClick={clearAll}
                style={{ 
                  padding: '8px 16px', 
                  background: '#f44336', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear All
              </button>
            </div>
          </div>
          
          {error && (
            <div style={{ color: '#f44336', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px', marginBottom: '10px' }}>
              {error}
            </div>
          )}
          
          <div className="editor-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="input-section" style={{ position: 'relative', minHeight: '300px' }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '10px' }}>
                Input {isEscapeMode ? 'HTML' : 'Escaped HTML'}
              </h3>
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', position: 'relative', height: '300px' }}>
                <TextareaWithLines
                  value={htmlInput}
                  onChange={(e) => setHtmlInput(e.target.value)}
                  placeholder={isEscapeMode ? 
                    "Enter HTML code to escape..." : 
                    "Enter escaped HTML entities to unescape..."
                  }
                  textareaRef={inputRef}
                  lineNumbersRef={inputLineNumbersRef}
                  onScroll={handleInputScroll}
                  style={{ minHeight: '300px' }}
                />
              </div>
            </div>
            
            {output && (
              <div className="output-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '1.25rem', margin: 0 }}>
                    Output {isEscapeMode ? 'Escaped HTML' : 'Unescaped HTML'}
                  </h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleCopyOutput}
                      style={{
                        padding: '6px 12px',
                        background: '#607d8b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {copySuccess ? 'Copied!' : 'Copy'}
                    </button>
                    
                    <button
                      onClick={downloadOutput}
                      style={{
                        padding: '6px 12px',
                        background: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Download
                    </button>
                  </div>
                </div>
                
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', position: 'relative', height: '300px' }}>
                  <PreWithLines
                    value={output}
                    preRef={outputRef}
                    lineNumbersRef={outputLineNumbersRef}
                    onScroll={handleOutputScroll}
                    style={{ 
                      minHeight: '300px',
                      backgroundColor: '#f8f9fa',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About HTML Escape/Unescape</h2>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            HTML escaping converts special characters to HTML entities to prevent them from being interpreted as HTML code.
            This is essential for security when displaying user-generated content and for preserving literal text.
          </p>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            Common HTML entities that are escaped/unescaped:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '20px', lineHeight: '1.6' }}>
            <li><strong>&lt;</strong> becomes <strong>&amp;lt;</strong> (less than)</li>
            <li><strong>&gt;</strong> becomes <strong>&amp;gt;</strong> (greater than)</li>
            <li><strong>&amp;</strong> becomes <strong>&amp;amp;</strong> (ampersand)</li>
            <li><strong>"</strong> becomes <strong>&amp;quot;</strong> (double quote)</li>
            <li><strong>'</strong> becomes <strong>&amp;#39;</strong> (single quote)</li>
          </ul>
          <p style={{ lineHeight: '1.6' }}>
            <strong>Use Cases:</strong> Preventing XSS attacks, displaying HTML code as text, 
            processing form data, and safely rendering user input in web applications.
          </p>
        </div>
      </div>
            
      <style jsx>{`
        .tab-selector button:hover {
          background-color: ${isEscapeMode ? '#45a049' : '#f0f0f0'} !important;
        }
        
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .controls {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .button-container {
            flex-direction: column;
            width: 100%;
          }
          
          .button-container button {
            width: 100%;
          }
          
          .tab-selector {
            flex-direction: column;
            width: 100%;
          }
          
          .tab-selector button {
            width: 100%;
            border-bottom: none !important;
            border-radius: 4px;
            margin-bottom: 5px;
          }
          
          .tab-container {
            border-bottom: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HtmlEscapeUnescapePage;